import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  extractActivitiesFromResponse,
  fetchActivities,
  mapActivityToTickerEvent,
} from '../api/activities';
import {
  extractBannersFromResponse,
  fetchBanners,
  mapBannerToSlide,
} from '../api/banners';
import {
  extractContentsFromResponse,
  fetchContents,
  mapContentToGalleryImage,
} from '../api/contents';

const EMPTY_CONTENT = {
  bannerSlides: [],
  tickerEvents: [],
  gridImages: [],
};

const SiteContentContext = createContext(null);

export function SiteContentProvider({ children }) {
  const [content, setContent] = useState(EMPTY_CONTENT);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function loadSiteContentFromApi() {
      setIsLoading(true);

      try {
        const [bannerResponse, activityResponse, contentResponse] = await Promise.all([
          fetchBanners(),
          fetchActivities(),
          fetchContents(),
        ]);

        if (cancelled) {
          return;
        }

        const banners = extractBannersFromResponse(bannerResponse);
        const activities = extractActivitiesFromResponse(activityResponse);
        const contents = extractContentsFromResponse(contentResponse);

        setContent({
          bannerSlides: banners.map(mapBannerToSlide),
          tickerEvents: activities.map((activity) =>
            mapActivityToTickerEvent(activity, contents)
          ),
          gridImages: contents
            .map((content) => mapContentToGalleryImage(content))
            .filter((image) => image.src),
        });
      } catch {
        if (!cancelled) {
          setContent(EMPTY_CONTENT);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    loadSiteContentFromApi();

    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(
    () => ({
      bannerSlides: content.bannerSlides,
      tickerEvents: content.tickerEvents,
      gridImages: content.gridImages,
      isLoading,
    }),
    [content, isLoading]
  );

  return (
    <SiteContentContext.Provider value={value}>
      {children}
    </SiteContentContext.Provider>
  );
}

export function useSiteContent() {
  const context = useContext(SiteContentContext);
  if (!context) {
    throw new Error('useSiteContent must be used within SiteContentProvider');
  }
  return context;
}
